/*
  Upsell pop-up

  A multi-step drawer that takes over the product page add-to-cart action. Each
  step offers a set of products; on the last step the selection is added to the
  cart in a single /cart/add.js request together with the main product.
*/

if (!customElements.get('upsell-popup')) {
  customElements.define(
    'upsell-popup',
    class UpsellPopup extends DrawerElement {
      constructor() {
        super();

        this.currentIndex = 0;
        this.submitting = false;
      }

      /*============================================================================
        Config
      ==============================================================================*/

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get formId() {
        return this.getAttribute('data-form-id');
      }

      get interceptEnabled() {
        return this.hasAttribute('data-intercept-add-to-cart');
      }

      get includeMainProduct() {
        return this.hasAttribute('data-include-main-product');
      }

      get stepLabel() {
        return this.getAttribute('data-step-label') || '';
      }

      get variantPrices() {
        if (this._variantPrices) return this._variantPrices;

        this._variantPrices = {};

        try {
          const ids = JSON.parse(this.querySelector('[data-upsell-variant-ids]').textContent);
          const prices = JSON.parse(this.querySelector('[data-upsell-variant-prices]').textContent);

          ids.forEach((id, index) => {
            this._variantPrices[String(id)] = Number(prices[index]);
          });
        } catch (error) {
          console.log(error);
        }

        return this._variantPrices;
      }

      // Compare-at price per variant, falling back to the price itself. Feeds the
      // discount line in the footer.
      get variantStandardPrices() {
        if (this._variantStandardPrices) return this._variantStandardPrices;

        this._variantStandardPrices = {};

        try {
          const ids = JSON.parse(this.querySelector('[data-upsell-variant-ids]').textContent);
          const prices = JSON.parse(
            this.querySelector('[data-upsell-variant-standard-prices]').textContent
          );

          ids.forEach((id, index) => {
            this._variantStandardPrices[String(id)] = Number(prices[index]);
          });
        } catch (error) {
          console.log(error);
        }

        return this._variantStandardPrices;
      }

      /*============================================================================
        Elements
      ==============================================================================*/

      get steps() {
        return Array.from(this.querySelectorAll('[data-upsell-step]'));
      }

      get options() {
        return Array.from(this.querySelectorAll('[data-upsell-option]'));
      }

      get selectedOptions() {
        return this.options.filter((option) => option.getAttribute('aria-pressed') === 'true');
      }

      // Cards standing in for the product of the page itself.
      get baseOptions() {
        return Array.from(this.querySelectorAll('[data-upsell-base]'));
      }

      /* The step that also offers the product of the page itself is the "which product"
         step: picking another card there swaps the main product out instead of adding to
         it. Every later step is an add-on and does add up. */
      get baseStep() {
        return this.baseOptions[0]?.closest('[data-upsell-step]') || null;
      }

      get mainProductReplaced() {
        const step = this.baseStep;
        if (!step) return false;

        return Array.from(step.querySelectorAll('[data-upsell-option]')).some(
          (option) =>
            option.getAttribute('aria-pressed') === 'true' && !option.hasAttribute('data-upsell-base')
        );
      }

      // Whether the product of the page is still part of this order.
      get addsMainProduct() {
        return this.includeMainProduct && !this.mainProductReplaced;
      }

      // A base card is the "no upgrade" choice. When the pop-up already adds the main
      // product it must not add or charge a second one. Once the main product has been
      // swapped out the card does carry its own line again.
      isDuplicateOfMainProduct(option) {
        return this.addsMainProduct && option.hasAttribute('data-upsell-base');
      }

      get backButton() {
        return this.querySelector('[data-upsell-back]');
      }

      get forwardButton() {
        return this.querySelector('[data-upsell-forward]');
      }

      get forwardLabel() {
        return this.querySelector('[data-upsell-forward-label]');
      }

      get stepCount() {
        return this.querySelector('[data-upsell-step-count]');
      }

      get originalTotal() {
        return this.querySelector('[data-upsell-original]');
      }

      get discountRow() {
        return this.querySelector('[data-upsell-discount-row]');
      }

      get discountTotal() {
        return this.querySelector('[data-upsell-discount]');
      }

      get total() {
        return this.querySelector('[data-upsell-total]');
      }

      get tierMessage() {
        return this.querySelector('[data-upsell-tier]');
      }

      get errorMessage() {
        return this.querySelector('[data-upsell-error]');
      }

      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      // The buy-button lookalikes that open this drawer. The theme's ModalElement
      // already wires up the click through aria-controls; here we only keep their
      // price and sold-out state in sync with the selected variant.
      get openButtons() {
        return Array.from(document.querySelectorAll(`[data-upsell-open][aria-controls="${this.id}"]`));
      }

      /*============================================================================
        Lifecycle
      ==============================================================================*/

      connectedCallback() {
        super.connectedCallback();

        // This theme's BaseElement names its AbortController "eventsController"
        // (older Concept versions called it "abortController").
        const signal = (this.eventsController || this.abortController).signal;

        this.addEventListener('click', this.onClick.bind(this), { signal });
        document.addEventListener('submit', this.onDocumentSubmit.bind(this), { capture: true, signal });
        document.addEventListener('click', this.onDocumentClick.bind(this), { capture: true, signal });

        this.steps.forEach((step) => {
          const track = step.querySelector('[data-upsell-track]');
          if (!track) return;

          track.addEventListener('scroll', () => this.updateScrollbar(step), { passive: true, signal });
          this.initTrackDrag(step, track, signal);
          this.initScrollbarDrag(step, track, signal);
        });

        this.variantChangeUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );

        // The drawer is moved to <body> when it opens, so the theme editor hook is
        // bound on the document instead of on the section wrapper.
        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.onBlockSelect.bind(this), { signal });
          document.addEventListener('shopify:block:deselect', this.onBlockDeselect.bind(this), { signal });
        }

        this.preselect();
        this.goTo(this.currentIndex, false);
      }

      // Selecting the pop-up block, a step or one of its products in the theme
      // editor opens the drawer on the matching step so the merchant sees the edit.
      isOwnBlock(target) {
        return target === this || (target instanceof Node && this.contains(target));
      }

      onBlockSelect(event) {
        if (!this.isOwnBlock(event.target)) return;

        const step = event.target.closest?.('[data-upsell-step]');
        if (step) this.goTo(this.steps.indexOf(step));

        if (!this.open) this.show();
      }

      onBlockDeselect(event) {
        if (!this.isOwnBlock(event.target)) return;

        this.hide();
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = undefined;
        }
      }

      afterShow() {
        super.afterShow();

        // Track widths are only measurable once the drawer is on screen.
        this.steps.forEach((step) => this.updateScrollbar(step));
      }

      /*============================================================================
        Add to cart interception
      ==============================================================================*/

      isMainProductForm(form) {
        if (!form) return false;
        if (this.formId && form.id === this.formId) return true;
        if (form.getAttribute('is') !== 'product-form') return false;
        if (this.productId && form.id.endsWith(`-${this.productId}`)) return true;

        const idInput = form.querySelector('[name="id"]');
        return !!idInput && Object.prototype.hasOwnProperty.call(this.variantPrices, idInput.value);
      }

      // Never take over the buy button while no products have been picked yet —
      // an empty pop-up would be a dead end.
      get canIntercept() {
        return this.interceptEnabled && !this.submitting && this.options.length > 0;
      }

      takeOver(event, form, activeElement) {
        event.preventDefault();
        event.stopPropagation();

        this.mainProductForm = form;
        this.handleErrorMessage();
        this.goTo(0, false);
        this.updateSummary();
        this.show(activeElement || null);
      }

      onDocumentSubmit(event) {
        if (!this.canIntercept) return;

        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !this.isMainProductForm(form)) return;

        this.takeOver(event, form, event.submitter);
      }

      // Belt and braces: catching the click too means the pop-up still opens if
      // anything else on the page swallows the form's submit event.
      onDocumentClick(event) {
        if (!this.canIntercept) return;

        const button = event.target.closest?.('button[type="submit"], input[type="submit"]');
        if (!button || button.disabled) return;
        if (button.closest('.shopify-payment-button, .upsell-popup')) return;

        const form = button.form || (button.getAttribute('form') ? document.getElementById(button.getAttribute('form')) : null);
        if (!this.isMainProductForm(form)) return;

        this.takeOver(event, form, button);
      }

      /*============================================================================
        Selection
      ==============================================================================*/

      preselect() {
        this.steps.forEach((step) => {
          if (step.dataset.selection !== 'single_required') return;
          if (!step.hasAttribute('data-preselect-first')) return;
          if (step.querySelector('[data-upsell-option][aria-pressed="true"]')) return;

          const first = Array.from(step.querySelectorAll('[data-upsell-option]')).find(
            (option) => !option.disabled
          );
          if (first) first.setAttribute('aria-pressed', 'true');
        });
      }

      onClick(event) {
        if (this.ignoreNextClick) {
          this.ignoreNextClick = false;
          return;
        }

        const option = this.optionFromClick(event);
        if (option) {
          this.onOptionClick(option);
          return;
        }

        if (event.target.closest('[data-upsell-forward]')) {
          this.onForward();
          return;
        }

        if (event.target.closest('[data-upsell-back]')) {
          this.goTo(this.currentIndex - 1);
          return;
        }
      }

      /* Normally the click lands on the card button itself. A pointer capture held on
         the track — ours while dragging, or one left behind by another script — makes
         the browser retarget the click to the track, so fall back to whatever card is
         under the cursor. Pointer clicks only: a keyboard "click" has no coordinates. */
      optionFromClick(event) {
        const direct = event.target.closest?.('[data-upsell-option]');
        if (direct) return direct;

        const track = event.target.closest?.('[data-upsell-track]');
        if (!track || event.detail === 0) return null;

        return document
          .elementFromPoint(event.clientX, event.clientY)
          ?.closest('[data-upsell-option]');
      }

      onOptionClick(option) {
        if (option.disabled) return;

        const step = option.closest('[data-upsell-step]');
        const mode = step ? step.dataset.selection : 'multiple_optional';
        const wasSelected = option.getAttribute('aria-pressed') === 'true';

        if (mode === 'multiple_optional') {
          option.setAttribute('aria-pressed', wasSelected ? 'false' : 'true');
        } else {
          step
            .querySelectorAll('[data-upsell-option]')
            .forEach((sibling) => sibling.setAttribute('aria-pressed', 'false'));

          if (mode === 'single_required') {
            option.setAttribute('aria-pressed', 'true');
          } else {
            option.setAttribute('aria-pressed', wasSelected ? 'false' : 'true');
          }
        }

        this.handleErrorMessage();
        this.updateSummary();
      }

      /*============================================================================
        Steps
      ==============================================================================*/

      goTo(index, scrollIntoView = true) {
        const steps = this.steps;
        if (steps.length === 0) return;

        this.currentIndex = Math.max(0, Math.min(index, steps.length - 1));

        steps.forEach((step, i) => step.toggleAttribute('hidden', i !== this.currentIndex));

        const isLast = this.currentIndex === steps.length - 1;

        this.backButton?.toggleAttribute('hidden', this.currentIndex === 0);

        if (this.forwardLabel) {
          this.forwardLabel.textContent = isLast
            ? this.getAttribute('data-submit-label') || 'Add to cart'
            : this.getAttribute('data-next-label') || 'Next';
        }

        if (this.stepCount) {
          this.stepCount.textContent = this.stepLabel
            .replace('[current]', this.currentIndex + 1)
            .replace('[total]', steps.length);
        }

        this.handleErrorMessage();
        this.updateSummary();
        this.updateScrollbar(steps[this.currentIndex]);

        if (scrollIntoView) {
          this.querySelector('.drawer__scrollable')?.scrollTo({ top: 0, behavior: 'instant' });
        }
      }

      onForward() {
        const step = this.steps[this.currentIndex];

        if (
          step &&
          step.dataset.selection === 'single_required' &&
          !step.querySelector('[data-upsell-option][aria-pressed="true"]')
        ) {
          this.handleErrorMessage(this.getAttribute('data-error-required'));
          return;
        }

        if (this.currentIndex < this.steps.length - 1) {
          this.goTo(this.currentIndex + 1);
          return;
        }

        this.addToCart();
      }

      /*============================================================================
        Carousel
      ==============================================================================*/

      /* Drag to pan the row. Touch is left alone — native scrolling is better there. */
      initTrackDrag(step, track, signal) {
        const THRESHOLD = 5;
        let pointerId = null;
        let startX = 0;
        let startScroll = 0;
        let distance = 0;
        let captured = false;

        // Stops the browser from starting a native image drag mid-swipe
        track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

        track.addEventListener(
          'pointerdown',
          (event) => {
            if (event.pointerType === 'touch' || event.button !== 0) return;

            pointerId = event.pointerId;
            startX = event.clientX;
            startScroll = track.scrollLeft;
            distance = 0;
            captured = false;
          },
          { signal }
        );

        track.addEventListener(
          'pointermove',
          (event) => {
            if (pointerId === null || event.pointerId !== pointerId) return;

            const delta = event.clientX - startX;
            distance = Math.max(distance, Math.abs(delta));

            // Capture only once this is really a drag. Capturing on pointerdown would
            // retarget the click to the track, so a plain click on a card never
            // reaches the card button itself.
            if (!captured) {
              if (distance <= THRESHOLD) return;

              captured = true;
              track.classList.add('is-dragging');
              track.setPointerCapture(pointerId);
            }

            track.scrollLeft = startScroll - delta;
          },
          { signal }
        );

        const release = (event) => {
          if (pointerId === null || event.pointerId !== pointerId) return;

          if (captured) track.releasePointerCapture?.(pointerId);
          pointerId = null;
          captured = false;
          track.classList.remove('is-dragging');

          // A drag ends in a click on whichever card was under the cursor — swallow it
          if (distance > THRESHOLD) {
            this.ignoreNextClick = true;
            setTimeout(() => {
              this.ignoreNextClick = false;
            });
          }
        };

        track.addEventListener('pointerup', release, { signal });
        track.addEventListener('pointercancel', release, { signal });
        // Without capture the pointer can be released outside the track, so the
        // window is what reliably ends the drag.
        window.addEventListener('pointerup', release, { signal });
      }

      /* The bar under the row doubles as a scrollbar: click to jump, drag to pan. */
      initScrollbarDrag(step, track, signal) {
        const scrollbar = step.querySelector('[data-upsell-scrollbar]');
        const thumb = step.querySelector('[data-upsell-thumb]');
        if (!scrollbar || !thumb) return;

        let pointerId = null;

        const scrollToPointer = (clientX) => {
          const bar = scrollbar.getBoundingClientRect();
          const maxScroll = track.scrollWidth - track.clientWidth;
          const travel = bar.width - thumb.offsetWidth;
          if (maxScroll <= 0 || travel <= 0) return;

          const ratio = (clientX - bar.left - thumb.offsetWidth / 2) / travel;
          track.scrollLeft = Math.min(1, Math.max(0, ratio)) * maxScroll;
        };

        scrollbar.addEventListener(
          'pointerdown',
          (event) => {
            pointerId = event.pointerId;
            scrollbar.setPointerCapture(pointerId);
            scrollbar.classList.add('is-dragging');
            scrollToPointer(event.clientX);
            event.preventDefault();
          },
          { signal }
        );

        scrollbar.addEventListener(
          'pointermove',
          (event) => {
            if (pointerId === null || event.pointerId !== pointerId) return;
            scrollToPointer(event.clientX);
          },
          { signal }
        );

        const release = (event) => {
          if (pointerId === null || event.pointerId !== pointerId) return;
          scrollbar.releasePointerCapture?.(pointerId);
          pointerId = null;
          scrollbar.classList.remove('is-dragging');
        };

        scrollbar.addEventListener('pointerup', release, { signal });
        scrollbar.addEventListener('pointercancel', release, { signal });
      }

      updateScrollbar(step) {
        if (!step) return;

        const track = step.querySelector('[data-upsell-track]');
        const thumb = step.querySelector('[data-upsell-thumb]');
        const scrollbar = step.querySelector('[data-upsell-scrollbar]');
        if (!track || !thumb) return;

        const overflowing = track.scrollWidth > track.clientWidth + 1;
        scrollbar?.toggleAttribute('hidden', !overflowing);

        if (!overflowing) return;

        // Clamp the offset against the thumb width so it never overhangs the bar
        const widthPercent = Math.max(10, (track.clientWidth / track.scrollWidth) * 100);
        const offsetPercent = Math.min(
          (track.scrollLeft / track.scrollWidth) * 100,
          100 - widthPercent
        );
        thumb.style.width = `${widthPercent}%`;
        thumb.style.insetInlineStart = `${Math.max(0, offsetPercent)}%`;
      }

      /*============================================================================
        Summary
      ==============================================================================*/

      // When the drawer is opened by the button there is no submitted form, so fall
      // back to the product form this pop-up is bound to.
      get productFormElement() {
        return (
          this.mainProductForm ||
          (this.formId ? document.forms[this.formId] : null) ||
          document.querySelector(`form[is="product-form"][id$="-${this.productId}"]`)
        );
      }

      get mainVariantId() {
        const input = this.productFormElement?.querySelector('[name="id"]');
        if (input && input.value) return input.value;

        return Object.keys(this.variantPrices)[0];
      }

      get mainQuantity() {
        const input = this.productFormElement?.querySelector('[name="quantity"]');
        const quantity = input ? parseInt(input.value, 10) : 1;

        return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
      }

      onVariantChange(event) {
        if (String(event.data?.productId) !== String(this.productId)) return;

        this.updateOpenButtons(event.data.variant);
        this.updateBaseOptions(event.data.variant);
        this.updateSummary();
      }

      get moneyFormat() {
        return theme.settings.currencyCodeEnabled
          ? theme.settings.moneyWithCurrencyFormat
          : theme.settings.moneyFormat;
      }

      // Keep the "this product" card in step with the variant picker.
      updateBaseOptions(variant) {
        if (!variant) return;

        const money = (cents) => theme.Currency.formatMoney(cents, this.moneyFormat);
        const onSale = variant.compare_at_price > variant.price;

        this.baseOptions.forEach((option) => {
          option.dataset.variantId = variant.id;
          option.dataset.price = variant.price;
          option.dataset.standardPrice = onSale ? variant.compare_at_price : variant.price;
          option.disabled = !variant.available;
          option.classList.toggle('upsell-popup__slide-sold-out', !variant.available);

          const price = option.querySelector('.price');
          if (price) {
            price.classList.toggle('price--on-sale', onSale);
            price.innerHTML =
              (onSale
                ? `<span class="price__sale inline-flex items-center relative">${money(variant.compare_at_price)}</span>`
                : '') + `<span class="price__regular whitespace-nowrap">${money(variant.price)}</span>`;
          }

          const image = option.querySelector('.upsell-popup__media img');
          if (image && variant.featured_image?.src) {
            image.removeAttribute('srcset');
            image.src = variant.featured_image.src;
          }
        });
      }

      updateOpenButtons(variant) {
        if (!variant) return;

        this.openButtons.forEach((button) => {
          const price = button.querySelector('[data-upsell-button-price]');
          if (price) price.innerHTML = theme.Currency.formatMoney(variant.price, this.moneyFormat);

          button.disabled = !variant.available;

          const label = button.querySelector('[data-upsell-button-label]');
          if (label) {
            label.textContent = variant.available
              ? button.getAttribute('data-label') || label.textContent
              : theme.variantStrings.soldOut;
          }
        });
      }

      /* Adds up what is selected right now, both at the compare-at price and at the price
         actually charged. The gap between the two is the discount shown in the footer. */
      get totals() {
        let standard = 0;
        let final = 0;

        if (this.addsMainProduct) {
          const price = this.variantPrices[this.mainVariantId];

          if (typeof price === 'number' && !Number.isNaN(price)) {
            const standardPrice = this.variantStandardPrices[this.mainVariantId];

            final += price * this.mainQuantity;
            standard += (standardPrice || price) * this.mainQuantity;
          }
        }

        this.selectedOptions.forEach((option) => {
          if (this.isDuplicateOfMainProduct(option)) return;

          const price = parseInt(option.dataset.price, 10) || 0;
          const quantity = parseInt(option.dataset.quantity, 10) || 1;
          const standardPrice = parseInt(option.dataset.standardPrice, 10) || price;

          final += price * quantity;
          standard += standardPrice * quantity;
        });

        return { standard: standard, final: final, savings: Math.max(0, standard - final) };
      }

      updateSummary() {
        const totals = this.totals;
        const money = (cents) => theme.Currency.formatMoney(cents, this.moneyFormat);

        if (this.originalTotal) this.originalTotal.innerHTML = money(totals.standard);
        if (this.total) this.total.innerHTML = money(totals.final);
        if (this.discountTotal) this.discountTotal.innerHTML = `-${money(totals.savings)}`;

        this.discountRow?.toggleAttribute('hidden', totals.savings <= 0);

        this.updateTierMessage(totals);
      }

      /*============================================================================
        Tiered discount
      ==============================================================================*/

      // Units, not cards: a card set to add two of something moves the count by two,
      // which is what the automatic discount in the admin counts too.
      get itemCount() {
        return this.items.reduce((count, item) => count + item.quantity, 0);
      }

      fillTemplate(template, values) {
        return Object.keys(values).reduce(
          (text, key) => text.replace(`[${key}]`, values[key]),
          template || ''
        );
      }

      /* One line, at most two parts: what is already unlocked and what the next tier
         would take. The discount itself is an automatic discount applied at checkout, so
         this only ever promises — it is deliberately kept out of the totals above. */
      updateTierMessage(totals) {
        const element = this.tierMessage;
        if (!element) return;

        // The first step swaps one product for another, so nothing there moves the count.
        const onFirstStep = this.currentIndex === 0;
        const tiers = window.BundleTiers;

        if (!tiers || onFirstStep) {
          element.toggleAttribute('hidden', true);
          return;
        }

        const count = this.itemCount;
        const current = tiers.current(count);
        const next = tiers.next(count);
        const parts = [];

        if (current) {
          parts.push(
            this.fillTemplate(element.dataset.tierActive, {
              percent: current.percent,
              amount: theme.Currency.formatMoney(
                tiers.savings(totals.final, current.percent),
                this.moneyFormat
              )
            })
          );
        }

        if (next) {
          parts.push(
            this.fillTemplate(element.dataset.tierNudge, {
              count: next.minItems - count,
              percent: next.percent
            })
          );
        }

        const filled = parts.filter((part) => part.trim() !== '');

        element.innerHTML = filled.join(' · ');
        element.toggleAttribute('hidden', filled.length === 0);
        element.classList.toggle('upsell-popup__tier--unlocked', !!current);
      }

      /*============================================================================
        Add to cart
      ==============================================================================*/

      get items() {
        const items = [];

        if (this.addsMainProduct && this.mainVariantId) {
          items.push({ id: this.mainVariantId, quantity: this.mainQuantity });
        }

        this.selectedOptions.forEach((option) => {
          if (this.isDuplicateOfMainProduct(option)) return;

          items.push({
            id: option.dataset.variantId,
            quantity: parseInt(option.dataset.quantity, 10) || 1
          });
        });

        return items;
      }

      addToCart() {
        const items = this.items;
        if (items.length === 0) {
          this.handleErrorMessage(this.getAttribute('data-error-empty'));
          return;
        }

        if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
          theme.utils.postLink2(theme.routes.cart_add_url, { parameters: { items: items } });
          return;
        }

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          items: items,
          sections: sectionsToBundle,
          sections_url: window.location.pathname
        });

        this.submitting = true;
        this.forwardButton?.setAttribute('aria-disabled', 'true');
        this.forwardButton?.setAttribute('aria-busy', 'true');

        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.handleErrorMessage(parsedState.description || parsedState.message);
              document.dispatchEvent(
                new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsedState.description } })
              );
              return;
            }

            const cartJson = await (
              await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })
            ).json();
            cartJson['sections'] = parsedState['sections'];

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'upsell-popup',
              cart: cartJson
            });
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));

            await this.hide();
            this.cartDrawer?.show();
          })
          .catch((error) => {
            console.log(error);
            this.handleErrorMessage(this.getAttribute('data-error-generic'));
          })
          .finally(() => {
            this.submitting = false;
            this.forwardButton?.removeAttribute('aria-busy');
            this.forwardButton?.removeAttribute('aria-disabled');
          });
      }

      handleErrorMessage(message = false) {
        if (!this.errorMessage) return;

        this.errorMessage.toggleAttribute('hidden', !message);
        this.errorMessage.textContent = message || '';
      }
    }
  );
}
